import { useState, useEffect } from 'react';
import { Download, X, Smartphone } from 'lucide-react';
import { Button } from './ui/button';

export const InstallPWA = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return; // Already installed
    }

    // Check if iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);

    // Check if banner was dismissed recently
    const dismissed = localStorage.getItem('pwa-banner-dismissed');
    if (dismissed) {
      const dismissedDate = new Date(dismissed);
      const daysSinceDismissed = (new Date() - dismissedDate) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) return; // Don't show for 7 days
    }

    // Listen for the beforeinstallprompt event (Android/Chrome)
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowBanner(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Show iOS banner after a delay
    if (isIOSDevice) {
      const timer = setTimeout(() => setShowBanner(true), 3000);
      return () => clearTimeout(timer);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (isIOS) {
      setShowIOSInstructions(true);
      return;
    }

    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setShowBanner(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowBanner(false);
    setShowIOSInstructions(false);
    localStorage.setItem('pwa-banner-dismissed', new Date().toISOString());
  };

  if (!showBanner) return null;

  // iOS Instructions Modal
  if (showIOSInstructions) {
    return (
      <div className="fixed inset-0 bg-black/80 flex items-end sm:items-center justify-center z-[100] p-4">
        <div className="bg-[#0A0A0A] border border-white/10 rounded-2xl p-6 max-w-sm w-full animate-in slide-in-from-bottom duration-300">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-white">Instalar en iPhone/iPad</h3>
            <button onClick={handleDismiss} className="p-1 hover:bg-white/10 rounded-full">
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>
          
          <div className="space-y-4 text-gray-300 text-sm">
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 bg-[#7C3AED] rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">1</span>
              <p>Toca el botón <strong className="text-white">Compartir</strong> en Safari (el cuadro con flecha hacia arriba)</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 bg-[#7C3AED] rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">2</span>
              <p>Desplázate y toca <strong className="text-white">"Agregar a pantalla de inicio"</strong></p>
            </div>
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 bg-[#7C3AED] rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">3</span>
              <p>Toca <strong className="text-white">"Agregar"</strong> en la esquina superior derecha</p>
            </div>
          </div>

          <div className="mt-6 p-3 bg-[#7C3AED]/10 border border-[#7C3AED]/30 rounded-xl">
            <p className="text-[#7C3AED] text-xs text-center">
              ¡Listo! La app aparecerá en tu pantalla de inicio como cualquier otra app
            </p>
          </div>

          <Button
            onClick={handleDismiss}
            className="w-full mt-4 bg-[#7C3AED] hover:bg-[#7C3AED]/80"
          >
            Entendido
          </Button>
        </div>
      </div>
    );
  }

  // Install Banner
  return (
    <div className="fixed bottom-20 left-4 right-4 sm:left-auto sm:right-4 sm:w-80 z-[60] animate-in slide-in-from-bottom duration-500">
      <div className="bg-gradient-to-r from-[#7C3AED] to-[#5B21B6] rounded-2xl p-4 shadow-2xl shadow-[#7C3AED]/20">
        <button 
          onClick={handleDismiss}
          className="absolute -top-2 -right-2 w-6 h-6 bg-black rounded-full flex items-center justify-center border border-white/20"
        >
          <X className="w-3 h-3 text-white" />
        </button>
        
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
            <Smartphone className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-white font-bold text-sm">Instala la App</h4>
            <p className="text-white/70 text-xs">Acceso rápido desde tu pantalla de inicio</p>
          </div>
        </div>
        
        <Button
          onClick={handleInstall}
          className="w-full mt-3 bg-white text-[#7C3AED] hover:bg-white/90 font-semibold"
          data-testid="install-pwa-btn"
        >
          <Download className="w-4 h-4 mr-2" />
          {isIOS ? 'Ver instrucciones' : 'Instalar gratis'}
        </Button>
      </div>
    </div>
  );
};
